<template>
  <div
    class="list-of-boards-item-row"
    :class="[
      { 'quick-view-enabled': expanded && !isPastSprints },
      { 'grid-view': gridView && !isSprint },
    ]"
    @mouseenter="onMouseEnter(currentBoardEventData)"
    @mouseleave="onMouseLeave"
    @click="boardClick"
    @mousedown="preventSelection"
  >
    <div class="list-of-boards-item-wrap">
      <div
        class="list-of-boards-item"
        :class="[
          { 'board-dragging': pageBoardDragging },
          { 'active-sprint': isActiveSprint },
          { 'sprint-item': isPastSprints || isNextSprint },
          { pt: !isPastSprints },
          { 'bg-background-strongest h-14': isPastSprints },
        ]"
      >
        <div class="board-item-header-wrap">
          <vue-scroll
            :class="{ 'sprint-item-header-wrap-scroll': isSprint }"
            :ops="sprintHeaderScrollOptions"
          >
            <div class="board-item-icon-title-wrap">
              <em
                v-if="!isMobile && !isPastSprints"
                :style="getCustomIconStyle(iconPath(!expanded ? 'chevron-right' : 'chevron-down'))"
                class="custom-icons bg-icon-default w-4 h-4 shrink-0"
              />
              <em
                v-if="isSprint"
                :style="[
                  getCustomIconStyle(iconPath(sprintStateIconName)),
                  { background: sprintStateIconColor },
                ]"
                class="custom-icons w-4 h-4 shrink-0"
              />
              <div class="board-titles-wrap">
                <div class="board-title-in-list">
                  {{ itemTitle }}
                </div>
              </div>
              <div
                v-tooltip="{
                  content: translate('copyBoardUrl'),
                  placement: 'bottom',
                }"
                class="copy-link-button pt"
                @click.stop="copyLinkUrl"
              >
                <em
                  :style="getCustomIconStyle(iconPath('link-st'))"
                  class="custom-icons copy-header-icon"
                />
              </div>
              <BookmarkedIcon
                class="board-grid-bookmark"
                :entity="board"
                :entity-name="bookmarkResourcename"
              />
              <span class="list-card-count">{{ boardCountText }}</span>
              <div class="members-quick-view-wrap">
                <div
                  v-if="!isMobile"
                  class="board-item-open-icon pt"
                  @click.stop="openBoard"
                >
                  <div class="text-text-default text-sm">{{ openItemText }}</div>
                  <em
                    :style="getCustomIconStyle(iconPath('arrow-right'))"
                    class="custom-icons bg-icon-default w-4 h-4"
                  />
                </div>
              </div>
              <div
                v-if="isPastSprints || isNextSprint"
                class="header-sprint-lists"
              >
                <ListOfBoardsItemList
                  v-for="(listId, index) in sprintListIds"
                  :key="listId"
                  :board-id="board.id"
                  :list-id="listId"
                  :project-id="projectId"
                  :quick-view="false"
                  :index="index"
                  :sprint-item-header="true"
                  :list-type="itemType"
                  :sprint-disable-drag="true"
                  :preview-project-boards="previewProjectBoards"
                  @verticalScrolling="handleListScroll"
                />
              </div>
              <div class="sprints-header-right-side flex items-center ml-auto">
                <div
                  v-if="isSprint"
                  class="sprint-date-period text-sm text-text-textStrong mr-2.5 text-nowrap"
                >
                  {{ sprintDatePeriod }}
                </div>
                <v-popover
                  v-if="isSprint && sprintActionsItems.length && !isGuestReader"
                  class="flex items-center justify-center mr-2.5"
                  interactive
                  trigger="click"
                  tag="div"
                  content-tag="div"
                  :role="'poper'"
                  :append-to="sprintActionsAppendTo"
                  :on-trigger="stopPropagation"
                  :on-untrigger="stopPropagation"
                >
                  <div
                    class="group hover:bg-icon-bg h-6 w-6 flex items-center justify-center rounded cursor-pointer"
                  >
                    <em
                      class="custom-icons"
                      :style="getCustomIconStyle(iconPath('more-horizontal'))"
                    />
                  </div>

                  <template #content="{ state, hide }">
                    <ListDropdown
                      v-if="state.isVisible && sprintActionsItems.length"
                      :position="'project-dropdown'"
                      :items="sprintActionsItems"
                      :width="240"
                      @click="handleSprintActionsDropdownClick($event, hide)"
                      @closeEscape="hide"
                    />
                  </template>
                </v-popover>
              </div>
            </div>
          </vue-scroll>
        </div>
        <div
          v-if="!isMobile && !isPastSprints"
          class="board-item-content"
          @wheel="preventScrolling"
          @touchmove="preventScrolling"
          @scroll="preventScrolling"
        >
          <vue-scroll
            ref="horizontalListsScroll"
            :ops="scrollOptions"
            @handle-scroll="handleScroll"
          >
            <draggable
              :key="board.id"
              v-model="itemListIds"
              :item-key="(listId) => listId"
              group="lists"
              :sort="expanded"
              :disabled="(!expanded && !listDragging) || isMobile || isSprint || isGuestReader"
              class="draggable-lists"
              :animation="false"
              :prevent-on-filter="false"
              :force-fallback="true"
              :fallback-tolerance="10"
              draggable=".board-item-list-content-wrap"
              @change="changeHandler"
              @start="startDragging"
              @end="endDragging"
            >
              <template #item="{ element, index }">
                <ListOfBoardsItemList
                  :key="element"
                  :board-id="board.id"
                  :list-id="element"
                  :project-id="projectId"
                  :quick-view="expanded"
                  :index="index"
                  :list-type="itemType"
                  :sprint-disable-drag="sprintDisableDrag"
                  :preview-project-boards="previewProjectBoards"
                  @verticalScrolling="handleListScroll"
                />
              </template>
            </draggable>
          </vue-scroll>
        </div>
      </div>
    </div>
    <Portal
      v-if="confirmDialog.isRevealed.value"
      to="root-widget"
    >
      <ConfirmationModal
        :title="translate('cancelSprint')"
        :description="translate('cancelSprintWarning')"
        :confirm-button-text="translate('cancelSprint')"
        :cancel-button-text="translate('dontCancel')"
        @cancel="confirmDialog.cancel()"
        @confirm="confirmDialog.confirm()"
      />
    </Portal>
  </div>
</template>

<script>
import getDashedTitle from '@/utilities/getDashedTitle';
import socketWatcher from '@/mixins/SocketWatcher';
import boardItemListMixin from '@/mixins/boardItemListMixin';
import ListOfBoardsItemList from './ListOfBoardsItemList';
import customIconsMixin from '@/mixins/customIconsMixin';
import BookmarkedIcon from '@/components/widgets/BookmarkedIcon';
import generateLink from '@/utilities/generateLink';
import EventBus from '@/utilities/eventBus';
import { convertTimestampToDateSecondFormat } from '@/utilities/timeDifference';
import ListDropdown from '@/components/widgets/ListDropdown';
import { appColors } from '@/utilities/colorsAndIcons';
import { SprintState } from '@/types/sprint';
import { ResourceType } from '@/types/resources';
import ConfirmationModal from '@/components/widgets/ConfirmationModal';
import { useConfirmDialog } from '@vueuse/core';
import { Portal } from 'portal-vue';
import useSprintStateIcon from '@/utilities/composables/useSprintStateIcon';
import { ListBehavior } from '@superthread-com/api';
import format from 'date-fns/format';
import useGetSprintStatusByDates from '@/utilities/composables/useGetSprintStatusByDates';
import draggingEnd from '@/mixins/draggingEnd';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'ListOfBoardsItem',
  components: {
    ListOfBoardsItemList,
    BookmarkedIcon,
    ListDropdown,
    ConfirmationModal,
    Portal,
  },

  mixins: [socketWatcher, boardItemListMixin, customIconsMixin, draggingEnd],
  props: {
    itemId: {
      type: String,
      required: true,
    },
    boardIndex: {
      type: Number,
      required: false,
      default: -1,
    },
    previewProjectBoards: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemType: {
      type: String,
      required: false,
      default: 'board',
    },
  },

  setup() {
    const confirmDialog = useConfirmDialog();
    const { sprintStateIcon } = useSprintStateIcon();
    const { sprintStatus } = useGetSprintStatusByDates();
    const { isGuestReader } = useCurrentRole();

    return {
      confirmDialog,
      sprintStateIcon,
      sprintStatus,
      isGuestReader,
    };
  },

  data() {
    return {
      scrollOptions: {
        scrollPanel: {
          scrollingY: false,
          scrollingX: true,
        },
        rail: {
          gutterOfSide: '3px',
          gutterOfEnds: '12px',
        },
        bar: {
          keepShow: true,
        },
      },
      fullyLoaded: false,
      loading: false,
      scrolling: false,
      listScrolling: false,
      cardDraggedOver: false,
      openBoardClick: false,
      scrollTimeout: null,
      scrollHorizontalTimeout: null,
      refreshScrollTimeout: null,
      verticalScrollProcess: 0,
      // used for checking if board content is scrolling
      boardContentScrolling: false,
      boardContentScrollingTimeout: null,
      cardClick: false,
      boardGroupBy: 'status',
    };
  },
  computed: {
    sprintActionsAppendTo() {
      return document.body;
    },
    projectId() {
      return this.$store.getters.getCurrentProjectId;
    },
    isSprint() {
      return this.itemType === ResourceType.Sprint;
    },
    board() {
      return this.$store.getters.getFlatBoards[this.itemId] || {};
    },

    isPastSprints() {
      return this.isSprint && (this.isCompletedSprint || this.isCancelledSprint);
    },

    isCompletedSprint() {
      return this.isSprint && this.board.state === SprintState.Completed;
    },

    isCancelledSprint() {
      return this.isSprint && this.board.state === SprintState.Canceled;
    },

    isNextSprint() {
      return this.isSprint && this.sprintStatus(this.board) === SprintState.Planned;
    },

    sprintDisableDrag() {
      return (
        this.isSprint &&
        [SprintState.Canceled, SprintState.Completed].includes(this.sprintStatus(this.board))
      );
    },

    isActiveSprint() {
      return (
        this.sprintStatus(this.board) === SprintState.Active &&
        this.board.state === SprintState.Active &&
        this.isSprint
      );
    },

    sprintStateIconName() {
      const sprintStatus =
        this.isCancelledSprint || this.isCompletedSprint
          ? this.board.state
          : this.sprintStatus(this.board);
      return this.sprintStateIcon(sprintStatus)?.icon;
    },

    sprintStateIconColor() {
      const sprintStatus =
        this.isCancelledSprint || this.isCompletedSprint
          ? this.board.state
          : this.sprintStatus(this.board);
      return this.sprintStateIcon(sprintStatus)?.color;
    },

    sprintDatePeriod() {
      const { start_date, end_date } = this.board;

      const cancelledLabel =
        this.sprintStatus(this.board) === SprintState.Canceled
          ? `(${this.translate('cancelled')})`
          : '';

      return `${this.getWeekDate(start_date)} ${convertTimestampToDateSecondFormat(
        start_date
      )}\u2013${this.getWeekDate(end_date)} ${convertTimestampToDateSecondFormat(end_date)} ${cancelledLabel}`;
    },

    itemTitle() {
      if (this.isSprint) return this.board.title;
      return this.board.title;
    },
    openItemText() {
      return this.isSprint ? this.translate('openSprint') : this.translate('openBoard');
    },
    itemListIds: {
      get() {
        return this.boardsListSpaceHome;
      },
      // set should intentionally do nothing
      set() {},
    },

    sprintListIds() {
      if (this.isNextSprint) {
        return this.boardsListSpaceHome.filter(
          (id) =>
            this.$store.getters.lists[id] &&
            this.$store.getters.lists[id].behavior === ListBehavior.Committed
        );
      }
      if (this.isCompletedSprint) {
        return this.boardsListSpaceHome.filter(
          (id) =>
            this.$store.getters.lists[id] &&
            [
              ListBehavior.Completed,
              ListBehavior.Cancelled,
              this.$constants.rolledOverList,
            ].includes(this.$store.getters.lists[id].behavior)
        );
      }
      if (this.isCancelledSprint) {
        return this.boardsListSpaceHome.filter(
          (id) =>
            this.$store.getters.lists[id] &&
            [ListBehavior.Cancelled, ListBehavior.Completed].includes(
              this.$store.getters.lists[id].behavior
            )
        );
      }
      return [];
    },

    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    onDifferentRoute() {
      return (
        this.$route.name !== this.$constants.routeNames.space ||
        this.$store.getters.displayProjectPreviewBoards
      );
    },
    listDragging() {
      if (this.onDifferentRoute) return false;
      return this.draggingIsOn && this.draggedEntity.entityName === 'list';
    },
    pageBoardDragging() {
      return (
        !this.onDifferentRoute &&
        this.draggingIsOn &&
        (this.draggedEntity.entityName === 'board' || this.draggedEntity.entityName === 'page')
      );
    },
    expanded() {
      return !!this.$store.getters.boardExpanded(this.board.id);
    },
    boardListsLength() {
      return this.boardsListSpaceHome?.length || 0;
    },
    gridView() {
      return this.$store.getters.getCurrentProjectBoardColumnLayout === 'grid';
    },
    previewPopoverKey() {
      return this.$store.getters.getPreviewPopoverKey;
    },

    draggingEnd() {
      return this.$store.getters.getDraggingEnd;
    },
    quickCardCreating() {
      return this.$store.getters.quickCardBoardId === this.itemId;
    },
    getBoardsListsIds() {
      return this.$store.getters.boardsListsIds;
    },
    getListCards() {
      return this.$store.getters.getListCards;
    },
    boardCardsCount() {
      return this.$store.getters.boardsCardsIds[this.itemId]?.length || 0;
    },
    boardCountText() {
      return `${this.boardCardsCount} ${this.translate('cards').toLowerCase()}`;
    },
    boardRow() {
      return Math.floor(this.boardIndex / 2);
    },
    currentBoardEventData() {
      return {
        boardId: this.itemId,
        boardRow: this.boardRow,
        projectId: this.board.project_id,
      };
    },
    scrollPanelOptions() {
      return this.gridView && !this.isSprint
        ? { scrollingY: this.expanded, scrollingX: !this.expanded }
        : { scrollingY: false, scrollingX: true };
    },
    sprintActionsItems() {
      const sprintActionsItems = [
        {
          label: this.translate('renameSprint'),
          id: 'renameSprint',
          customIconName: this.iconPath('edit'),
          states: [SprintState.Planned, SprintState.Active, SprintState.Completed],
        },
        {
          label: this.translate('markAsComplete'),
          id: 'markAsComplete',
          customIconName: this.iconPath('check-circle'),
          states: [SprintState.Active],
        },
        {
          label: this.translate('changeSprintDates'),
          id: 'changeSprintDates',
          customIconName: this.iconPath('calendar'),
          states: [SprintState.Planned],
        },
        {
          label: this.translate('cancelSprint'),
          id: 'cancelSprint',
          customIconName: this.iconPath('x-circle'),
          customIconColor: appColors.error600,
          states: [SprintState.Active],
          deleteOrCancelType: true,
        },
      ];

      const currentSprintState = this.board.state;

      return sprintActionsItems.filter(({ states }) => states.includes(currentSprintState));
    },

    bookmarkResourcename() {
      return this.isSprint ? ResourceType.Sprint : ResourceType.Board;
    },

    sprintHeaderScrollOptions() {
      return {
        scrollPanel: {
          scrollingY: false,
          scrollingX: this.isSprint,
        },
        rail: {
          gutterOfSide: '0px',
          gutterOfEnds: '12px',
        },
        bar: {
          keepShow: false,
        },
      };
    },
  },
  watch: {
    boardListsLength(newVal) {
      if (!newVal) {
        if (!this.expanded) return;
        this.$store.dispatch('toggleBoardQuickView', { boardId: this.board.id });
      }
    },
    gridView() {
      this.refreshScroll();
    },
    expanded() {
      this.updateScrollPanelOptions();
    },
  },
  created() {
    EventBus.$on('boardMouseEnter', this.onMouseEnter);
    if (this.expanded) {
      this.updateScrollPanelOptions();
    }
  },
  mounted() {
    EventBus.$on('cardClicked', this.cardClickHandler);
    this.refreshScrollTimeout = setTimeout(() => {
      this.refreshScroll();
    }, 0);

    this.confirmDialog.onConfirm(() => {
      this.updateSprintStatus('canceled');
    });

    if (this.isActiveSprint) {
      this.fetchSprint();
    }
  },
  beforeUnmount() {
    EventBus.$off('cardClicked', this.cardClickHandler);
    EventBus.$off('boardMouseEnter', this.onMouseEnter);
    if (this.refreshScrollTimeout) {
      clearTimeout(this.refreshScrollTimeout);
      this.refreshScrollTimeout = null;
    }
  },
  methods: {
    stopPropagation(instance, event) {
      event.stopPropagation?.();
    },
    cardClickHandler(boardId) {
      if (boardId === this.board.id) {
        this.cardClick = true;
      }
    },
    preventSelection(e) {
      if (e.target?.tagName !== 'TEXTAREA') {
        e.preventDefault();
      }
    },
    closeAddCardIfOpen() {
      if (this.$store.getters.getListDraftCardCreateView) {
        this.$store.dispatch('setListDraftCardCreateView', '');
      }
    },
    boardClick() {
      if (this.isPastSprints) return;
      if (this.isMobile) {
        this.openBoard();
        return;
      }

      if (this.scrolling || this.listScrolling || this.draggingEnd || this.openBoardClick) {
        this.scrolling = false;
        this.listScrolling = false;
        this.openBoardClick = false;
        this.cardClick = false;
        return;
      }
      if (!this.boardListsLength && !this.isSprint) {
        this.openBoard();
        return;
      }
      if (!this.cardClick) {
        // no card set for preview, toggle quick view
        this.toggleQuickView();
        this.closeAddCardIfOpen();
      } else {
        // set current board and list for open card preview
        this.setBoard();
      }
      this.$store.dispatch('setPreviewPopoverKey', false);
      this.cardClick = false;
    },
    checkDragScroll() {
      const { hBarDragging, vBarDragging } = this.$refs.horizontalListsScroll.classHooks;
      if (hBarDragging || vBarDragging) {
        clearTimeout(this.scrollHorizontalTimeout);
        this.scrollHorizontalTimeout = setTimeout(() => {
          this.checkDragScroll();
        }, 300);
      } else {
        this.scrolling = false;
      }
    },
    handleScroll() {
      if (!this.scrolling) {
        this.scrolling = true;
        this.checkDragScroll();
      }
      this.initBoardContentScrolling();
    },
    setBoard() {
      this.$store.dispatch('setBoardListsFlat', [...this.board.lists]);
      this.$store.dispatch('setCurrentProjectId', this.board.project_id);
      this.$store.dispatch('setCurrentBoardId', this.board.id);
    },
    openBoard($evt) {
      this.cardClick = false;
      this.openBoardClick = true;
      if (this.scrolling || this.listScrolling) {
        this.scrolling = false;
        this.listScrolling = false;
        return;
      }

      this.setBoard();

      if (!this.boardListsLength && this.board.title !== '') {
        // open list input when no lists in board
        this.$store.dispatch('setDraftListInputVisibleOnOpen', true);
      }
      if (
        !this.previewPopoverKey ||
        (!$evt.target.classList.contains('board-item-list-card-content') &&
          !$evt.target.classList.contains('board-item-card-title'))
      ) {
        const dashedItemTitle = getDashedTitle(this.itemTitle);
        const itemRoute = this.isSprint ? `sprint-${this.board.id}` : `board-${this.board.id}`;
        this.$router.push(`/${this.teamSlug}/${itemRoute}-${dashedItemTitle}`);
        this.openBoardClick = false;
      }
    },
    toggleQuickView() {
      if (!this.boardListsLength && !this.isSprint) return;
      if (this.openBoardClick) {
        this.openBoardClick = false;
        return;
      }
      if (this.gridView && !this.isSprint) {
        this.toggleBoardsInRow();
        return;
      }
      this.$store.dispatch('toggleBoardQuickView', { boardId: this.board.id });
    },
    toggleBoardsInRow() {
      this.$store.dispatch('toggleBoardsInRow', {
        boardId: this.board.id,
        value: !this.expanded,
        projectId: this.board.project_id,
      });
    },
    startDragging() {
      if (!this.expanded) return;
      // save which entity is dragged and what is type of the entity
      // that way we can enable/disable dropping to specific boards
      this.$store.dispatch('setDraggedEntity', {
        type: this.board.type,
        entityName: 'list',
        id: this.board.id,
      });
    },
    endDragging() {
      this.onDraggingEnd();
      // remove previously saved entity that is dragged and it's type
      this.$store.dispatch('setDraggedEntity', {});
    },
    changeHandler(evt) {
      if (evt.moved) {
        const { element, oldIndex, newIndex } = evt.moved;
        this.updateBoardListsOrder({ oldPosition: oldIndex, position: newIndex, listId: element });
      }
      if (evt.removed) {
        const { oldIndex, element } = evt.removed;
        this.$store.dispatch('setRemovedBoardListPosition', {
          position: oldIndex,
          boardId: this.board.id,
          listId: element,
        });
        if (!this.boardListsLength)
          this.$store.dispatch('toggleBoardQuickView', { boardId: this.board.id });
      }
      if (evt.added) {
        const { element, newIndex } = evt.added;
        const list = this.$store.getters.lists[element];
        const oldBoardId = list.board_id;

        this.$store.dispatch('addBoardListId', {
          boardId: this.board.id,
          listId: element,
          position: newIndex,
        });
        this.$store.dispatch('removeBoardListId', {
          boardId: oldBoardId,
          id: element,
        });
        this.$store.dispatch('setBoardListProp', {
          listId: element,
          propertyName: 'board_id',
          value: this.board.id,
        });

        const listCardsIds = this.$store.getters.getListCards[element];

        this.$store
          .dispatch('updateList', {
            boardId: oldBoardId,
            listId: element,
            list: { board_id: this.board.id, position: newIndex },
          })
          .then(() => {
            if (oldBoardId !== this.board.id) {
              // update local listCards with new boardId
              this.$store.dispatch('localUpdateCardFieldInMultipleCards', {
                cardIds: listCardsIds,
                fieldName: 'board_id',
                fieldValue: this.board.id,
              });
            }
          })
          .catch(() => {
            const { position } = this.$store.getters.removedListMeta;
            this.$store.dispatch('addBoardListId', {
              boardId: oldBoardId,
              listId: element,
              position,
            });
            this.$store.dispatch('removeBoardListId', {
              boardId: this.board.id,
              id: element,
            });
            this.$store.dispatch('setBoardListProp', {
              listId: element,
              propertyName: 'board_id',
              value: oldBoardId,
            });

            // revert board_id in cards if necessary
            if (oldBoardId !== this.board.id) {
              this.$store.dispatch('localUpdateCardFieldInMultipleCards', {
                cardIds: listCardsIds,
                fieldName: 'board_id',
                fieldValue: oldBoardId,
              });
            }
            this.$store.dispatch('setRemovedBoardListPosition', {});
          });
      }
    },
    checkMove() {
      return this.expanded;
    },
    expandBoardOnDragOver() {
      this.cardDraggedOver = true;
      this.expandTimout = setTimeout(() => {
        if (!this.expanded) {
          if (this.fullyLoaded) {
            this.toggleQuickView();
          } else {
            const loadWatcher = this.$watch('fullyLoaded', () => {
              this.toggleQuickView();
              loadWatcher();
            });
          }
        }
      }, 500);
    },
    onMouseEnter(data) {
      const { boardId, boardRow, projectId, customEvent } = data || {};
      if (boardId === this.board.id && boardRow === this.boardRow && customEvent) return;
      if (!customEvent && this.gridView) {
        this.emitCustomMouseEnterEvent();
      }
      if (boardRow === this.boardRow && projectId === this.board.project_id) {
        if (this.isSprint) {
          this.fetchSprint();
        } else {
          this.fetchBoard();
        }
        if (this.draggedEntity.entityName === 'card' && !this.checkMove()) {
          this.expandBoardOnDragOver();
        }
      }
    },
    emitCustomMouseEnterEvent() {
      EventBus.$emit('boardMouseEnter', {
        ...this.currentBoardEventData,
        customEvent: true,
      });
    },
    onMouseLeave() {
      if (this.expandTimout) {
        clearTimeout(this.expandTimout);
        this.expandTimout = null;
      }
      if (this.cardDraggedOver) {
        this.cardDraggedOver = false;
      }
    },
    fetchBoard() {
      if (!this.fullyLoaded && !this.loading && !this.quickCardCreating) {
        this.tryToSubscribe();
        this.loading = true;
        this.$store.dispatch('fetchBoard', { boardId: this.board.id }).then(() => {
          this.fullyLoaded = true;
          this.loading = false;
        });
      }
    },

    fetchSprint() {
      if (!this.fullyLoaded && !this.loading && !this.sprintChangeStatus) {
        this.tryToSubscribe();
        this.loading = true;
        this.$store
          .dispatch('getSprint', {
            sprintId: this.board.orginalId,
            projectId: this.board.project_id,
          })
          .then(() => {
            this.fullyLoaded = true;
            this.loading = false;
          });
      }
    },

    subscribeToResource() {
      const isSprintBoard = this.board.id.includes('s');
      const resourceType = isSprintBoard ? 'sprints' : 'boards';
      this.$socket.subscribe(resourceType, this.board.id);
    },

    checkListVerticalScroll(listRef) {
      const { vBarDragging } = listRef.classHooks;
      if (vBarDragging) {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.checkListVerticalScroll(listRef);
        }, 300);
      } else {
        this.listScrolling = false;
      }
    },

    handleListScroll({ listRef }) {
      this.listScrolling = true;
      this.checkListVerticalScroll(listRef);
      this.initBoardContentScrolling();
    },

    initBoardContentScrolling() {
      this.boardContentScrolling = true;
      if (this.boardContentScrollingTimeout) clearTimeout(this.boardContentScrollingTimeout);
      this.boardContentScrollingTimeout = setTimeout(() => {
        this.boardContentScrolling = false;
      }, 600);
    },

    clearCardsFromLists(lists) {
      const newLists = [...lists];
      newLists.forEach((l) => {
        Object.assign(l, { ...l, cards: null });
      });

      return newLists;
    },

    copyLinkUrl() {
      const link = generateLink(
        this.isSprint ? ResourceType.Sprint : ResourceType.Board,
        this.teamSlug,
        this.board.id,
        this.board.title
      );
      navigator.clipboard.writeText(link);
      this.$store.dispatch('addUiNotification', {
        message: this.translate('linkCopiedToClipboard'),
        icon: 'link-st',
        duration: 4000,
      });
    },

    refreshScroll() {
      if (this.$refs.horizontalListsScroll) {
        this.$refs.horizontalListsScroll.refresh();
      }
    },

    updateScrollPanelOptions() {
      this.scrollOptions.scrollPanel = this.scrollPanelOptions;
      this.scrollOptions.rail.gutterOfSide = this.gridView && this.expanded ? '0px' : '3px';
    },

    preventScrolling(event) {
      const { v: verticalScrollProcess } = this.$refs.horizontalListsScroll.getScrollProcess();
      if (
        (this.expanded && this.gridView && verticalScrollProcess !== this.verticalScrollProcess) ||
        this.boardContentScrolling
      ) {
        this.verticalScrollProcess = verticalScrollProcess;
        event.preventDefault();
        event.stopPropagation();
      }
    },

    handleSprintActionsDropdownClick(item, hideFn) {
      switch (item.id) {
        case 'renameSprint':
          this.$store.dispatch('setSprintForEdit', {
            id: this.itemId,
            orginalId: this.board.orginalId,
            action: 'rename',
          });
          break;
        case 'markAsComplete':
          this.updateSprintStatus('completed');
          break;
        case 'changeSprintDates':
          this.$store.dispatch('setSprintForEdit', {
            id: this.itemId,
            orginalId: this.board.orginalId,
            action: 'changeDates',
          });
          break;
        case 'cancelSprint':
          this.confirmDialog.reveal();
          break;
        default:
          break;
      }
      if (typeof hideFn === 'function') {
        hideFn();
      }
    },

    addUpdateStatusNotification(status) {
      let message = '';
      switch (status) {
        case SprintState.Completed:
          message = this.translate('sprintMarkedAsCompleted');
          break;
        case SprintState.Canceled:
          message = this.translate('sprintMarkedAsCancelled');
          break;
        default:
      }
      if (!message) return;
      this.$store.dispatch('addUiNotification', {
        message,
        duration: 4000,
      });
    },

    updateSprintStatus(status) {
      const oldState = this.board.state;
      this.$store.dispatch('localUpdateSprint', {
        projectId: this.$store.getters.getCurrentProjectId,
        sprintId: this.board.id,
        prop: 'state',
        value: status,
      });
      if (this.expanded) {
        this.$store.dispatch('toggleBoardQuickView', { boardId: this.board.id });
      }
      this.$store
        .dispatch('updateSprint', {
          projectId: this.$store.getters.getCurrentProjectId,
          sprintId: this.board.orginalId,
          payload: { state: status },
        })
        .then((data) => {
          const { sprint } = data;
          this.$store.dispatch('postFetchSprint', sprint);
          this.addUpdateStatusNotification(status);

          this.$store
            .dispatch('getSprintsForProject', {
              projectId: this.$store.getters.getCurrentProjectId,
            })
            .then((innerData) => {
              const { sprints } = innerData;

              const currentSprintIndex = sprints.findIndex(
                (item) => item.id === this.board.orginalId
              );
              const nextSprint = sprints[currentSprintIndex + 1];

              if (!nextSprint) return;

              this.$store.dispatch('getSprint', {
                sprintId: nextSprint.orginalId || nextSprint.id,
                projectId: nextSprint.project_id,
              });
            });
        })
        .catch(() => {
          this.$store.dispatch('localUpdateSprint', {
            projectId: this.$store.getters.getCurrentProjectId,
            sprintId: this.board.id,
            prop: 'state',
            value: oldState,
          });
        });
    },

    getWeekDate(date) {
      return format(new Date(date * 1000), 'E', {
        weekStartsOn: 1,
      });
    },
  },
};
</script>

<style lang="scss">
$boardItemHeight: 109px;
$boardItemExpandedHeigth: 433px;
$gridBoardItemExpandedHeight: 600px;
$boardIconDimension: 40px;

.list-of-boards-item-row {
  display: flex;
  flex-direction: row;
  font-family: $primaryFontFamily;

  &:not(.quick-view-enabled) {
    .list-of-boards-item-wrap {
      .board-item-content {
        .board-item-list-content-wrap {
          .board-list-title {
            &.drop-target {
              width: inherit;
            }
          }
        }
      }
    }
  }
  .list-of-boards-item-wrap {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    position: relative;

    .list-of-boards-item {
      border: 1px solid;
      @apply flex flex-col box-border w-full bg-standard-bg border-standard-border select-none overflow-hidden rounded-lg;
      padding: 15px 15px 0px;
      height: $boardItemHeight;
      min-height: $boardItemHeight;
      max-height: $boardItemExpandedHeigth;

      &.sprint-item {
        @apply h-14 min-h-0;

        .board-item-header-wrap {
          @apply border-b-0;
        }
      }

      .draggable-lists {
        display: flex;
        height: 36px;
      }

      .board-item-content {
        height: 100%;
        margin-left: -12px;
        .__vuescroll {
          .__rail-is-horizontal {
            z-index: 3 !important;
          }
        }
      }

      .board-item-header-wrap {
        border-bottom: 1px solid;
        @apply flex items-center justify-between box-border w-full flex-row grow-0 shrink-0 pb-3 border-dividers-default;

        .board-item-icon-title-wrap {
          @apply flex items-center min-w-0 min-h-0 overflow-hidden gap-x-2 pr-2;

          .copy-link-button {
            @apply w-6 h-6 flex justify-center items-center rounded shrink-0;

            &:hover {
              @apply bg-icon-bg;

              .copy-header-icon {
                @apply bg-icon-hover;
              }
            }
          }

          .board-grid-bookmark {
            @apply flex justify-center m-0;
            max-width: 24px;
            min-width: 24px;
          }

          .board-titles-wrap {
            @apply overflow-hidden;

            .project-title {
              font-size: 13px;
              line-height: 16px;
              letter-spacing: 0.3px;
              color: #929292;
              margin-bottom: 1px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              padding-right: 16px;
            }

            .board-title-in-list {
              @apply text-base font-semibold text-text-default truncate;
            }
          }

          .list-card-count {
            @apply text-base text-inactive-text shrink-0;
          }

          .header-sprint-lists {
            @apply flex border-0 border-l border-solid border-standard-border ml-2 pl-1;

            .board-item-list-content-wrap {
              @apply pt-0.5;
            }
          }
        }

        .members-quick-view-wrap {
          display: flex;
          align-items: center;
          align-self: flex-start;
          flex-shrink: 0;

          .board-item-open-icon {
            border: 1px solid;
            padding: 1px 7px;
            @apply flex items-center justify-center gap-x-1 rounded bg-important-bg ml-2 border-important-border;

            &:hover {
              @apply bg-important-hover;
            }
            &:active {
              @apply bg-hover-onHover;
            }
          }
        }
      }

      &.active-sprint {
        @apply border-2 border-solid border-highlight-default;
      }
    }

    > .member-avatar {
      display: none;
    }
  }
  &.quick-view-enabled {
    max-height: initial;
    height: $boardItemExpandedHeigth;

    .list-of-boards-item-wrap {
      .list-of-boards-item {
        height: $boardItemExpandedHeigth;
        @apply bg-important-bg;

        .draggable-lists {
          height: 370px;
          align-items: flex-start;
        }

        .board-item-content {
          max-height: unset;
          height: 100%;
          .__vuescroll {
            .__rail-is-horizontal {
              z-index: 3 !important;
            }
          }
        }
      }
    }
  }

  &.drag-board {
    .list-of-boards-item-wrap {
      .list-of-boards-item {
        @apply pt-0 px-[31px] justify-center;
        min-height: unset;

        .board-item-header-wrap {
          margin-bottom: 0;
          border: unset;
          padding: unset;

          .board-item-icon-title-wrap {
            align-items: center;
            padding: unset;

            > :not(.board-titles-wrap) {
              display: none;
            }

            .board-titles-wrap {
              margin: unset;
              padding: unset;
            }
          }
          .members-quick-view-wrap {
            display: none;
          }
        }
        .board-item-content {
          display: none;
        }
      }
    }
  }

  &.grid-view {
    &.quick-view-enabled {
      height: $gridBoardItemExpandedHeight;

      .list-of-boards-item-wrap {
        .list-of-boards-item {
          height: $gridBoardItemExpandedHeight;
          max-height: $gridBoardItemExpandedHeight;

          .board-item-content {
            .draggable-lists {
              height: 538px;
              display: block;

              .board-item-list-content-wrap {
                &.expanded {
                  max-width: 100%;
                  width: 100%;

                  .board-list-title {
                    .droppable-header {
                      .list-title-count-wrap {
                        max-width: unset;
                      }
                    }
                  }
                  .board-list-cards {
                    height: 100% !important;
                    max-height: 100%;

                    &.card-is-dragged-blc {
                      min-height: 100%;
                    }

                    .draggable-cards {
                      &.card-is-dragged {
                        min-height: 100%;
                      }
                      .board-item-list-card {
                        .board-item-list-card-content {
                          width: unset;
                        }
                      }
                      .droppable-header-in-list {
                        width: unset;
                        margin-right: $boardColumnPaddingRight;
                        padding-right: 0px;

                        .list-title-count-wrap {
                          max-width: unset;
                        }
                      }
                      .list-footer {
                        padding-bottom: 3px;

                        .draft-card-create {
                          width: 100%;
                        }
                      }
                    }
                  }

                  &.sortable-ghost {
                    width: 100% !important;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .sprint-item-header-wrap-scroll {
    .__hidebar {
      padding-bottom: 0.5rem !important;
    }
  }
}

.mobile {
  .list-of-boards-item {
    @apply py-[7px]
      px-[15px]
      h-fit
      min-h-fit #{!important};

    .board-item-header-wrap {
      @apply m-0 p-0 border-0 #{!important};

      .board-item-icon-title-wrap {
        @apply p-0 #{!important};
      }
    }
  }
}
</style>
